import { useMemo } from "react";
import dayjs from "@/lib/day";
import styles from "@/styles/Payment.module.scss";
import formatPrice from "@/lib/formatPrice";
import calcCampaignEndAt from "@/lib/calcCampaignEndAt";

/**
 * CalcTotalAmountModal.
 *
 * コースを契約した場合の年間の金額をモーダルで表示
 *
 * @param {string} props.initialTransactionAt - 初回決済日
 * @param {Object[]} props.pricings - coursePricings
 * @param {Object} props.broadcastDiscountCampaign - 放送契約割引キャンペーン
 * @param {Object} props.applyCamapign - 適用されたキャンペーン
 * @returns {React.ReactElement}
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function CalcTotalAmountModal(_ref) {
  let {
    initialTransactionAt,
    pricings,
    broadcastDiscountCampaign,
    applyCamapign
  } = _ref;
  // 契約期間の計算
  // 初回決済日から1年間
  const contractPeriod = dayjs(new Date(initialTransactionAt)).add(1, "year").subtract(1, "day").format("YYYY/MM/DD");
  // 支払い金額の計算
  // 値段が安い順にソート
  const sortedPricings = [...pricings].sort((a, b) => {
    return a.payment_amount - b.payment_amount;
  });
  // 有効なキャンペーンのID
  const campaignIds = [broadcastDiscountCampaign, applyCamapign].filter(v => v).map(c => c.id);
  const totalAmount = useMemo(() => {
    const day = dayjs(new Date(initialTransactionAt));
    const keys = Array(12).keys();
    // 月ごとに計算して合計する
    return [...keys].map(i => {
      const month = day.add(i, "month");
      return calcPaymentAmount(month, sortedPricings, campaignIds, initialTransactionAt);
    }).reduce((a, b) => {
      return a + b;
    }, 0);
  }, [initialTransactionAt, sortedPricings, campaignIds]);
  return /*#__PURE__*/_jsx("div", {
    className: `${styles.container} ${styles.calcTotalAmount}`,
    children: /*#__PURE__*/_jsxs("div", {
      className: `${styles.section} ${styles.column}`,
      children: [/*#__PURE__*/_jsx("div", {
        className: styles.header,
        children: "\u3054\u5951\u7D04\u671F\u9593\u4E2D\u306E\u652F\u6255\u3044\u91D1\u984D\uFF08\u5E74\u984D\uFF09"
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.table,
        children: [/*#__PURE__*/_jsxs("div", {
          className: styles.row,
          children: [/*#__PURE__*/_jsx("div", {
            className: styles.label,
            children: "\u3054\u5951\u7D04\u671F\u9593"
          }), /*#__PURE__*/_jsxs("div", {
            className: styles.value,
            children: [initialTransactionAt, " \u301C ", contractPeriod]
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: styles.row,
          children: [/*#__PURE__*/_jsx("div", {
            className: styles.label,
            children: "\u304A\u652F\u6255\u3044\u91D1\u984D"
          }), /*#__PURE__*/_jsx("div", {
            className: styles.value,
            children: /*#__PURE__*/_jsxs("span", {
              className: styles.priceAmount,
              children: [formatPrice(totalAmount), "\u5186", /*#__PURE__*/_jsx("span", {
                children: "\uFF08\u7A0E\u8FBC\uFF09"
              })]
            })
          })]
        })]
      })]
    })
  });
}

/**
 * calcPaymentAmount.
 *
 * 複数の価格から、指定された日に有効になる価格を返す
 *
 * @param {Object} targetDate - dayjsのオブジェクト
 * @param {Object[]} pricings - coursePricings
 * @param {string[]} campaignIds - 有効なキャンペーンIDの配列
 * @param {string} initialTransactionAt - 初回決済日
 */
function calcPaymentAmount(targetDate, pricings, campaignIds, initialTransactionAt) {
  const targetPrice = pricings.find(price => {
    if (!validityCheck(targetDate, price.validity_start_at, price.validity_end_at)) {
      return false;
    }
    if (price.campaign) {
      // applyCountを考慮するひつようがある
      const campaignEndAt = calcCampaignEndAt(price.campaign, initialTransactionAt);
      const campaignValidity = validityCheck(targetDate, price.campaign.validityStartAt, campaignEndAt);
      if (campaignValidity && campaignIds.includes(price.campaign.id)) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  });
  return targetPrice.payment_amount;
}

/**
 * validityCheck.
 *
 * 日付の範囲の有効性をチェックする
 *
 * @param {Object} targetDate - dayjsのオブジェクト
 * @param {string} start - 日時の文字列
 * @param {string} end - 日時の文字列
 */
function validityCheck(targetDate, start, end) {
  const validityStartAt = dayjs(new Date(start));
  const validityEndAt = dayjs(new Date(end));
  if (start && end) {
    return targetDate.isBetween(validityStartAt, validityEndAt, null, "[)");
  } else if (start && !end) {
    return targetDate.isSameOrAfter(validityStartAt);
  } else if (!start && end) {
    return targetDate.isBefore(validityEndAt);
  } else {
    return true;
  }
}